import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Heart, MessageCircle, Share2, MoreHorizontal, Trash2, Send, ShieldBan, Lock, Repeat2, Flag, Pin, PinOff, Pencil, Check, X, VolumeX, Moon } from 'lucide-react';
import PremiumBadge from './PremiumBadge';
import AdminBadge from './AdminBadge';
import ModeratorBadge from './ModeratorBadge';
import MentorBadge from './MentorBadge';
import ReportModal from './ReportModal';
import IslamojiPicker from './IslamojiPicker';
import * as api from '../lib/api';
import { defaultAvatar, DEFAULT_BROTHER_AVATAR } from '../lib/avatarDefaults';
import { useIslamojis } from '../hooks/useIslamojis';
import { renderLineWithIslamojis, getSoloIslamoji, soloSayingStyle } from '../lib/islamojiRender';
import CommentTextarea from './CommentTextarea';

const getRelativeLabel = (dateStr) => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;

  const timeStr = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (diffDays === 1) return `Yesterday at ${timeStr}`;
  if (diffDays < 7) {
    const day = date.toLocaleDateString([], { weekday: 'long' });
    return `${day} at ${timeStr}`;
  }

  const sameYear = date.getFullYear() === now.getFullYear();
  return date.toLocaleDateString([], {
    month: 'long',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
};

const getTooltipLabel = (dateStr) => new Date(dateStr).toLocaleDateString([], {
  weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  hour: 'numeric', minute: '2-digit',
});

const PostCard = React.forwardRef(({ post, onDelete, onUpdate, onNavigate }, ref) => {
  const { profile } = useAuth();
  const [liked, setLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(post.likes_count || 0);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [showCommentIslamojis, setShowCommentIslamojis] = useState(false);
  const commentInputRef = useRef(null);
  const [showReplyIslamojis, setShowReplyIslamojis] = useState(false);
  const replyInputRef = useRef(null);
  const [commentsCount, setCommentsCount] = useState(post.comments_count || 0);
  const [showMenu, setShowMenu] = useState(false);
  const [loadingLike, setLoadingLike] = useState(false);
  const [loadingComment, setLoadingComment] = useState(false);
  const [banLoading, setBanLoading] = useState(false);
  const [restrictLoading, setRestrictLoading] = useState(false);
  const [reposting, setReposting] = useState(false);
  const [showRepostModal, setShowRepostModal] = useState(false);
  const [repostComment, setRepostComment] = useState('');
  const [repostSuccess, setRepostSuccess] = useState(false);
  const [isPinned, setIsPinned] = useState(post.is_pinned || false);
  const [isPinTop, setIsPinTop] = useState((post.pin_priority || 0) > 0);
  const islamojis = useIslamojis();
  const islamojiMap = React.useMemo(() => {
    const map = {};
    islamojis.forEach(item => { if (item.type === 'emoji') map[item.name.toLowerCase()] = item; });
    return map;
  }, [islamojis]);
  const [poll, setPoll] = useState(post.poll || null);
  const [userVote, setUserVote] = useState(post.user_vote !== undefined ? post.user_vote : null);
  const [voting, setVoting] = useState(false);
  const [commentLikes, setCommentLikes] = useState({});
  const [announcement, setAnnouncement] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null); // { id, display_name }
  const [replyText, setReplyText] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(post.content || '');
  const [editedAt, setEditedAt] = useState(post.edited_at || null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [isClamped, setIsClamped] = useState(false);
  const repostModalRef = useRef(null);
  const contentRef = useRef(null);

  const [relativeTime, setRelativeTime] = useState(() => getRelativeLabel(post.created_at));

  useEffect(() => {
    const tick = () => setRelativeTime(getRelativeLabel(post.created_at));
    const id = setInterval(tick, 60000);
    return () => clearInterval(id);
  }, [post.created_at]);

  const author = post.users || post.author || {};
  const isOwner = profile?.id === post.user_id;
  const isAdmin = profile?.is_admin === true;
  const isModerator = profile?.is_moderator === true;
  const isMentor = profile?.is_mentor === true;
  const isPremium = profile?.is_premium === true;
  const canEdit = isOwner && (isPremium || isMentor || isModerator || isAdmin);
  const showMenuButton = isOwner || isAdmin || isModerator;
  const isRepost = !!post.repost_of;
  const [showReport, setShowReport] = useState(false);

  useEffect(() => {
    checkIfLiked();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Focus trap for the repost modal
  useEffect(() => {
    if (!showRepostModal) return;
    const el = repostModalRef.current;
    if (!el) return;
    const focusable = Array.from(el.querySelectorAll(
      'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    ));
    focusable[0]?.focus();
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') { setShowRepostModal(false); return; }
      if (e.key !== 'Tab' || focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last?.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first?.focus(); }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showRepostModal]);

  // Detect whether the 6-line clamp is actually cutting off content.
  // scrollHeight includes hidden overflow; clientHeight is the visible box.
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    setIsClamped(el.scrollHeight > el.clientHeight);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkIfLiked = async () => {
    try {
      const res = await api.checkLiked(post.id, profile.id);
      setLiked(res.data.liked);
    } catch (err) {
      // ignore
    }
  };

  const handleLike = async () => {
    if (loadingLike) return;
    setLoadingLike(true);
    try {
      const res = await api.likePost(post.id, profile.id);
      setLiked(res.data.liked);
      setLikesCount(res.data.likes_count);
      setAnnouncement(res.data.liked
        ? `Post liked. ${res.data.likes_count} ${res.data.likes_count === 1 ? 'like' : 'likes'}.`
        : `Post unliked. ${res.data.likes_count} ${res.data.likes_count === 1 ? 'like' : 'likes'}.`
      );
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingLike(false);
    }
  };

  const handleToggleComments = async () => {
    if (!showComments) {
      try {
        const res = await api.getComments(post.id, profile?.id);
        const fetched = res.data || [];
        setComments(fetched);
        const likesMap = {};
        fetched.forEach(c => {
          likesMap[c.id] = { liked: c.is_liked || false, count: c.likes_count || 0 };
        });
        setCommentLikes(likesMap);
      } catch (err) {
        console.error(err);
      }
    }
    setShowComments(!showComments);
  };

  const handleLikeComment = async (commentId) => {
    try {
      const res = await api.likeComment(commentId, profile.id);
      setCommentLikes(prev => ({
        ...prev,
        [commentId]: { liked: res.data.liked, count: res.data.likes_count }
      }));
    } catch (err) {
      console.error(err);
    }
  };

  const insertIslamojiAt = (item, inputRef, value, setValue) => {
    const shortcode = `:${item.name}:`;
    const el = inputRef.current;
    if (el) {
      const start = el.selectionStart ?? value.length;
      const end = el.selectionEnd ?? value.length;
      const next = value.slice(0, start) + shortcode + value.slice(end);
      setValue(next);
      requestAnimationFrame(() => {
        el.focus();
        const pos = start + shortcode.length;
        el.setSelectionRange(pos, pos);
      });
    } else {
      setValue(value + shortcode);
    }
  };

  const handleSelectCommentEmoji = (item) => insertIslamojiAt(item, commentInputRef, newComment, setNewComment);
  const handleSelectReplyEmoji = (item) => insertIslamojiAt(item, replyInputRef, replyText, setReplyText);

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || loadingComment) return;
    if (!profile?.id) {
      alert('Please sign in to comment');
      return;
    }
    setLoadingComment(true);
    try {
      const res = await api.createComment(post.id, { content: newComment.trim() }, profile.id);
      const newCommentData = {
        ...res.data,
        users: {
          id: profile.id,
          display_name: profile.display_name,
          avatar_url: profile.avatar_url,
        },
      };
      setComments((prev) => [...prev, newCommentData]);
      setCommentsCount((prev) => {
        const next = prev + 1;
        setAnnouncement(`Comment added. ${next} ${next === 1 ? 'comment' : 'comments'}.`);
        return next;
      });
      setNewComment('');
      setShowCommentIslamojis(false);
    } catch (err) {
      console.error(err);
      alert('Failed to add comment. Please try again.');
    } finally {
      setLoadingComment(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editText.trim() || savingEdit) return;
    setSavingEdit(true);
    try {
      await api.editPost(post.id, editText.trim());
      setEditedAt(new Date().toISOString());
      setIsEditing(false);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to save edit');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleAddReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim() || loadingComment || !replyingTo) return;
    if (!profile?.id) { alert('Please sign in to reply'); return; }
    setLoadingComment(true);
    try {
      const res = await api.createComment(post.id, { content: replyText.trim(), parent_id: replyingTo.id }, profile.id);
      const newReply = {
        ...res.data,
        users: { id: profile.id, display_name: profile.display_name, avatar_url: profile.avatar_url },
      };
      setComments((prev) => prev.map((c) =>
        c.id === replyingTo.id ? { ...c, replies: [...(c.replies || []), newReply] } : c
      ));
      setReplyText('');
      setReplyingTo(null);
      setShowReplyIslamojis(false);
    } catch (err) {
      console.error(err);
      alert('Failed to add reply. Please try again.');
    } finally {
      setLoadingComment(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      try {
        await api.deletePost(post.id);
        onDelete?.(post.id);
      } catch (err) {
        console.error(err);
      }
    }
    setShowMenu(false);
  };

  const handleBan = async () => {
    const authorName = author.display_name || 'this user';
    if (!window.confirm(
      `Ban ${authorName}?\n\nThis will:\n- Permanently block their account\n- Delete all their posts and comments\n- Prevent them from logging in or signing up again\n\nThis cannot be undone.`
    )) return;
    setBanLoading(true);
    try {
      await api.banUser({ user_id: post.user_id });
      alert(`${authorName} has been banned from the community.`);
      onDelete?.(post.id);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to ban user.';
      alert(detail);
    } finally {
      setBanLoading(false);
      setShowMenu(false);
    }
  };

  const handleRestrictPosting = async () => {
    const authorName = author.display_name || 'this user';
    if (!window.confirm(
      `Restrict posting for ${authorName}?\n\n• 1st infraction: posting blocked for 30 days\n• 2nd infraction: posting blocked permanently\n\nThey will receive a notification. You can lift restrictions from their profile.`
    )) return;
    setRestrictLoading(true);
    try {
      const res = await api.restrictPosting(post.user_id);
      const isPermanent = res.data?.infraction_count >= 2;
      alert(`${authorName}'s posting has been ${isPermanent ? 'permanently restricted' : 'restricted for 30 days'}.`);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to restrict posting.');
    } finally {
      setRestrictLoading(false);
      setShowMenu(false);
    }
  };

  const handleRepost = async () => {
    if (reposting || isOwner) return;
    setReposting(true);
    try {
      // Repost the original post (if this is already a repost, repost the original)
      const targetPostId = post.repost_of || post.id;
      const trimmed = (repostComment || '').trim();
      await api.repostPost(targetPostId, profile.id, trimmed || null);
      setRepostSuccess(true);
      setRepostComment('');
      // Auto-close modal + clear success after a short delay
      setTimeout(() => {
        setShowRepostModal(false);
        setRepostSuccess(false);
        onUpdate?.();
      }, 1200);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to repost.';
      alert(detail);
    } finally {
      setReposting(false);
    }
  };

  const openRepostModal = () => {
    if (isOwner) return;
    setRepostComment('');
    setRepostSuccess(false);
    setShowRepostModal(true);
  };

  const handlePinPost = async () => {
    try {
      const res = await api.pinPost(post.id);
      setIsPinned(res.data.is_pinned);
      if (!res.data.is_pinned) setIsPinTop(false); // unpinning clears top-pin too
      onUpdate?.();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to pin post.');
    }
    setShowMenu(false);
  };

  const handlePinTop = async () => {
    try {
      const res = await api.pinPostTop(post.id);
      setIsPinTop(res.data.pin_priority > 0);
      if (res.data.is_pinned !== null && res.data.is_pinned !== undefined) setIsPinned(res.data.is_pinned);
      onUpdate?.();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to update top pin.');
    }
    setShowMenu(false);
  };

  const handleVote = async (optionIndex) => {
    if (userVote !== null || voting || !profile?.id) return;
    setVoting(true);
    try {
      const res = await api.voteOnPoll(post.id, optionIndex);
      setPoll(res.data.poll);
      setUserVote(res.data.user_vote);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to vote.');
    } finally {
      setVoting(false);
    }
  };

  const getPollPercent = (optionIndex) => {
    if (!poll || !poll.total_votes) return 0;
    return Math.round((poll.options[optionIndex].votes / poll.total_votes) * 100);
  };

  const handlePinComment = async (commentId) => {
    try {
      await api.pinComment(commentId);
      const res = await api.getComments(post.id);
      setComments(res.data || []);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to pin comment.');
    }
  };


  return (
    <motion.article
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="glass-card rounded-arch-xl p-6 card-hover"
      data-testid={`post-${post.id}`}
    >
      {/* Pinned indicator */}
      {isPinned && (
        <div className="flex items-center gap-1.5 mb-3 pb-3 border-b border-primary/10 text-xs text-primary font-medium"
          data-testid={`pinned-banner-${post.id}`}>
          <Pin className="w-3.5 h-3.5" aria-hidden="true" />
          <span>{isPinTop ? 'Pinned to top' : 'Pinned post'}</span>
        </div>
      )}

      {/* Repost banner */}
      {isRepost && (
        <div className="flex items-center gap-2 mb-3 pb-3 border-b border-primary/10 text-xs text-text-muted"
          data-testid={`repost-banner-${post.id}`}>
          <Repeat2 className="w-3.5 h-3.5 text-primary" aria-hidden="true" />
          <span>
            Reposted by{' '}
            <button
              type="button"
              onClick={() => {
                const name = author.display_name;
                if (name && onNavigate) onNavigate(`user:${name}`);
              }}
              className="font-medium text-primary hover:underline"
              data-testid={`repost-by-${post.id}`}>
              {author.display_name || 'Someone'}
            </button>
          </span>
        </div>
      )}

      {/* Optional reposter caption */}
      {isRepost && post.repost_comment && (
        <div className="mb-4 px-4 py-3 bg-primary/5 border-l-2 border-primary/30 rounded-r-arch text-sm text-text-main whitespace-pre-wrap"
          data-testid={`repost-comment-${post.id}`}>
          {post.repost_comment}
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          {isRepost ? (
            /* Show original author for reposts — clickable link to their profile */
            <>
              <button
                type="button"
                onClick={() => {
                  const name = post.original_author_name;
                  if (name && onNavigate) onNavigate(`user:${name}`);
                }}
                aria-label={`View ${post.original_author_name || 'author'}'s profile`}
                className="rounded-full focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <img
                  src={post.original_author_avatar || DEFAULT_BROTHER_AVATAR}
                  alt={post.original_author_name || 'User'}
                  className="w-12 h-12 rounded-full object-cover border-2 border-primary"
                />
              </button>
              <div>
                <button
                  type="button"
                  onClick={() => {
                    const name = post.original_author_name;
                    if (name && onNavigate) onNavigate(`user:${name}`);
                  }}
                  className="font-medium text-primary hover:underline"
                  data-testid={`repost-original-author-${post.id}`}>
                  {post.original_author_name || 'Anonymous'}
                </button>
                <time
                  dateTime={post.created_at}
                  title={getTooltipLabel(post.created_at)}
                  className="text-sm text-text-muted cursor-default"
                >
                  {relativeTime}
                </time>
              </div>
            </>
          ) : (
            /* Normal post author */
            <>
              <img
                src={author.avatar_url || defaultAvatar(author.gender)}
                alt={author.display_name || 'User'}
                className="w-12 h-12 rounded-full object-cover border-2 border-primary"
              />
              <div>
                <button
                  type="button"
                  onClick={() => {
                    const name = author.display_name;
                    if (name && onNavigate) onNavigate(`user:${name}`);
                  }}
                  className="font-medium text-primary hover:underline flex items-center gap-1.5 flex-wrap text-left">
                  {author.display_name || 'Anonymous'}
                  <AdminBadge show={author.is_admin} />
                  <ModeratorBadge show={!author.is_admin && author.is_moderator} />
                  <MentorBadge show={!author.is_admin && !author.is_moderator && author.is_mentor} />
                  <PremiumBadge show={author.is_premium && !author.is_admin} size={13} />
                </button>
                <time
                  dateTime={post.created_at}
                  title={getTooltipLabel(post.created_at)}
                  className="text-sm text-text-muted cursor-default"
                >
                  {relativeTime}
                </time>
              </div>
            </>
          )}
        </div>

        {showMenuButton && (
          <div className="relative">
            <button
              onClick={() => setShowMenu(!showMenu)}
              aria-label="Post options"
              aria-expanded={showMenu}
              className="min-h-[44px] min-w-[44px] flex items-center justify-center hover:bg-primary/10 rounded-full transition-colors"
              data-testid={`post-menu-${post.id}`}
            >
              <MoreHorizontal className="w-5 h-5 text-text-muted" aria-hidden="true" />
            </button>

            <AnimatePresence>
              {showMenu && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="absolute right-0 mt-1 w-48 bg-white dark:bg-gray-800 rounded-arch shadow-lg border border-primary/20 overflow-hidden z-10"
                >
                  {canEdit && (
                    <button
                      onClick={() => { setIsEditing(true); setShowMenu(false); }}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-primary hover:bg-primary/10"
                      data-testid={`edit-post-${post.id}`}
                    >
                      <Pencil className="w-4 h-4" aria-hidden="true" />
                      Edit Post
                    </button>
                  )}
                  {isAdmin && (
                    <button
                      onClick={handlePinPost}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-primary hover:bg-primary/10"
                      data-testid={`pin-post-${post.id}`}
                    >
                      {isPinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
                      {isPinned ? 'Unpin Post' : 'Pin Post'}
                    </button>
                  )}
                  {isAdmin && (
                    <button
                      onClick={handlePinTop}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-primary hover:bg-primary/10"
                      data-testid={`pin-top-post-${post.id}`}
                    >
                      {isPinTop ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
                      {isPinTop ? 'Remove from Top' : 'Pin to Top'}
                    </button>
                  )}
                  <button
                    onClick={handleDelete}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                    data-testid={`delete-post-${post.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete Post
                  </button>
                  {(isAdmin || isModerator) && !isOwner && (
                    <button
                      onClick={handleRestrictPosting}
                      disabled={restrictLoading}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20 border-t border-orange-100 dark:border-orange-900/30 disabled:opacity-50"
                      data-testid={`restrict-posting-${post.id}`}
                    >
                      <VolumeX className="w-4 h-4" aria-hidden="true" />
                      {restrictLoading ? 'Restricting...' : 'Restrict Posting'}
                    </button>
                  )}
                  {isAdmin && !isOwner && (
                    <button
                      onClick={handleBan}
                      disabled={banLoading}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 border-t border-red-100 dark:border-red-900/30 disabled:opacity-50"
                      data-testid={`ban-user-${post.id}`}
                    >
                      <ShieldBan className="w-4 h-4" aria-hidden="true" />
                      {banLoading ? 'Banning...' : 'Ban Member'}
                    </button>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* Content */}
      {post.is_self_post && (
        <div className="flex items-center gap-1 mb-2 text-xs text-secondary" data-testid={`self-post-badge-${post.id}`}>
          <Lock className="w-3 h-3" aria-hidden="true" />
          <span>Only visible to you</span>
        </div>
      )}
      {isEditing ? (
        <div className="mb-4">
          <textarea
            autoFocus
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            className="w-full px-3 py-2 bg-white/70 border border-primary/30 rounded-arch focus:ring-2 focus:ring-primary text-sm text-text-main resize-none"
            rows={Math.max(3, editText.split('\n').length)}
            maxLength={5000}
            aria-label="Edit post content"
          />
          <div className="flex items-center gap-2 mt-2">
            <button
              onClick={handleSaveEdit}
              disabled={savingEdit || !editText.trim()}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-white text-sm rounded-full disabled:opacity-50"
            >
              <Check className="w-3.5 h-3.5" aria-hidden="true" />
              {savingEdit ? 'Saving\u2026' : 'Save'}
            </button>
            <button
              onClick={() => { setIsEditing(false); setEditText(post.content || ''); }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-text-muted hover:text-text-main rounded-full border border-primary/20"
            >
              <X className="w-3.5 h-3.5" aria-hidden="true" />
              Cancel
            </button>
            <span className="ml-auto text-xs text-text-muted">{editText.length}/5000</span>
          </div>
        </div>
      ) : post.content ? (
        <div className="mb-4">
          <div
            ref={contentRef}
            className={`text-text-main whitespace-pre-wrap${expanded ? '' : ' line-clamp-6'}`}
          >
            {getSoloIslamoji(editText, islamojiMap) ? (
              <img
                src={getSoloIslamoji(editText, islamojiMap).image_url}
                alt={editText}
                className="w-[68px] h-[68px]"
              />
            ) : (
              editText.split('\n').map((line, i) => {
                if (line.startsWith('\u201C') && line.endsWith('\u201D')) {
                  return <p key={i} className="italic text-primary/80 border-l-2 border-primary/30 pl-3 my-2">{renderLineWithIslamojis(line, islamojiMap)}</p>;
                }
                return <span key={i}>{renderLineWithIslamojis(line, islamojiMap)}{i < editText.split('\n').length - 1 ? '\n' : ''}</span>;
              })
            )}
          </div>
          {editedAt && (
            <p className="text-xs text-text-muted mt-1 italic">Edited</p>
          )}
          {isClamped && (
            <button
              type="button"
              onClick={() => setExpanded(e => !e)}
              className="mt-1.5 text-sm font-medium text-primary hover:underline"
            >
              {expanded ? 'Show less' : 'Read more'}
            </button>
          )}
        </div>
      ) : null}

      {/* Post Image */}
      {post.image_url && (
        <div className="mb-4 rounded-arch-lg overflow-hidden">
          <img
            src={post.image_url}
            alt="Post attachment"
            loading="lazy"
            className="w-full max-h-[500px] object-contain bg-black/5 dark:bg-white/5 rounded-arch-lg"
            data-testid={`post-image-${post.id}`}
          />
        </div>
      )}

      {/* Poll */}
      {poll && (
        <div className="mb-4 p-4 bg-white/50 dark:bg-white/5 rounded-arch-lg border border-primary/10"
          data-testid={`poll-${post.id}`}>
          <p className="text-sm font-medium text-text-main mb-3">
            {poll.total_votes} vote{poll.total_votes !== 1 ? 's' : ''}
          </p>
          <div className="space-y-2.5">
            {poll.options.map((option, i) => {
              const pct = getPollPercent(i);
              const isChosen = userVote === i;
              const barColor = i % 2 === 0 ? 'bg-primary' : 'bg-secondary';
              const hasVoted = userVote !== null;

              return hasVoted ? (
                // Results view — animated bars
                <div key={i} className="space-y-1" data-testid={`poll-result-${post.id}-${i}`}>
                  <div className="flex justify-between text-xs text-text-muted">
                    <span className={isChosen ? 'font-semibold text-text-main' : ''}>{option.text}</span>
                    <span className={isChosen ? 'font-semibold text-text-main' : ''}>{pct}%</span>
                  </div>
                  <div className="h-2 w-full bg-primary/10 dark:bg-white/10 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${pct}%` }}
                      transition={{ duration: 0.8, ease: 'easeOut' }}
                      className={`h-full rounded-full ${barColor} ${isChosen ? 'opacity-100' : 'opacity-60'}`}
                    />
                  </div>
                </div>
              ) : (
                // Voting view — clickable buttons
                <button
                  key={i}
                  onClick={() => handleVote(i)}
                  disabled={voting || !profile?.id}
                  className="w-full text-left px-4 py-2.5 rounded-arch border border-primary/20 text-sm text-text-main hover:border-primary hover:bg-primary/5 transition-colors disabled:opacity-50"
                  data-testid={`poll-option-${post.id}-${i}`}
                >
                  {option.text}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Tags */}
      {post.tags && post.tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {post.tags.map((tag) => {
            const isFounder = tag === 'Founder';
            const isMentor = tag === 'Mentor';
            return (
              <span
                key={tag}
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isFounder
                    ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                    : isMentor
                    ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300'
                    : 'bg-secondary/20 text-secondary'
                }`}
                data-testid={`tag-${tag.toLowerCase().replace(/\s/g, '-')}`}
              >
                {tag}
              </span>
            );
          })}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-6 pt-4 border-t border-white/30">
        <button
          onClick={handleLike}
          disabled={loadingLike}
          aria-label={liked ? `Unlike post (${likesCount} likes)` : `Like post (${likesCount} likes)`}
          className={`flex items-center gap-2 transition-colors disabled:opacity-60 ${
            liked ? 'text-primary' : 'text-text-muted hover:text-primary'
          }`}
          data-testid={`like-btn-${post.id}`}
        >
          <Heart className={`w-5 h-5 ${liked ? 'fill-current' : ''}`} aria-hidden="true" />
          <span className="text-sm" aria-hidden="true">{likesCount}</span>
        </button>

        <button
          onClick={handleToggleComments}
          aria-label={`${showComments ? 'Hide' : 'Show'} comments (${commentsCount})`}
          aria-expanded={showComments}
          className="flex items-center gap-2 text-text-muted hover:text-primary transition-colors"
          data-testid={`comments-btn-${post.id}`}
        >
          <MessageCircle className="w-5 h-5" aria-hidden="true" />
          <span className="text-sm" aria-hidden="true">{commentsCount}</span>
        </button>

        {/* Share/Repost button — hidden for own posts and self posts */}
        {!isOwner && !post.is_self_post && (
          <button
            onClick={openRepostModal}
            disabled={reposting}
            className={`flex items-center gap-2 transition-colors disabled:opacity-60 ${
              reposting ? 'text-primary' : 'text-text-muted hover:text-primary'
            }`}
            data-testid={`share-btn-${post.id}`}
          >
            <Share2 className="w-5 h-5" aria-hidden="true" />
            <span className="text-sm">{reposting ? 'Sharing...' : 'Share'}</span>
          </button>
        )}

        {/* Report flag (only on others' posts) */}
        {!isOwner && (
          <button onClick={() => setShowReport(true)}
            aria-label="Report this post"
            className="ml-auto min-h-[44px] min-w-[44px] flex items-center justify-center text-text-muted hover:text-red-500 transition-colors"
            data-testid={`report-btn-${post.id}`}>
            <Flag className="w-4 h-4" aria-hidden="true" />
          </button>
        )}
      </div>

      <ReportModal open={showReport} postId={post.id} onClose={() => setShowReport(false)} />

      {/* Comments Section */}
      <AnimatePresence>
        {showComments && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-4 pt-4 border-t border-white/30"
          >
            {/* Comments List */}
            <div className="space-y-4 mb-4">
              {comments.length === 0 ? (
                <p className="text-text-muted text-sm text-center py-2">No comments yet. Be the first!</p>
              ) : (
                comments.map((comment) => {
                  const canDeleteComment = comment.user_id === profile?.id || isAdmin || isModerator;
                  return (
                  <div key={comment.id}>
                  <div className={`flex gap-3 group ${comment.is_pinned ? 'relative' : ''}`} data-testid={`comment-${comment.id}`}>
                    <img
                      src={comment.users?.avatar_url || defaultAvatar(comment.users?.gender)}
                      alt={comment.users?.display_name || 'User'}
                      className="w-8 h-8 rounded-full object-cover flex-shrink-0 border border-primary"
                    />
                    <div className={`flex-1 rounded-arch p-3 relative ${comment.is_pinned ? 'bg-primary/5 border border-primary/20' : 'bg-white/50'}`}>
                      {comment.is_pinned && (
                        <span className="absolute -top-2 left-3 flex items-center gap-1 px-2 py-0.5 bg-primary text-white text-[10px] rounded-full font-medium"
                          data-testid={`pinned-comment-badge-${comment.id}`}>
                          <Pin className="w-2.5 h-2.5" aria-hidden="true" /> Pinned
                        </span>
                      )}
                      <p className="font-medium text-sm text-primary flex items-center gap-1.5 flex-wrap">
                        {comment.users?.display_name || 'Anonymous'}
                        <AdminBadge show={comment.users?.is_admin} size={10} />
                        <ModeratorBadge show={!comment.users?.is_admin && comment.users?.is_moderator} size={10} />
                        <MentorBadge show={!comment.users?.is_admin && !comment.users?.is_moderator && comment.users?.is_mentor} size={10} />
                        <PremiumBadge show={comment.users?.is_premium && !comment.users?.is_admin} size={12} />
                      </p>
                      {getSoloIslamoji(comment.content, islamojiMap) ? (
                        <img
                          src={getSoloIslamoji(comment.content, islamojiMap).image_url}
                          alt={comment.content}
                          className="w-12 h-12 mt-1"
                          style={soloSayingStyle(getSoloIslamoji(comment.content, islamojiMap), 48)}
                        />
                      ) : (
                        <p className="text-sm text-text-main mt-1 whitespace-pre-wrap">{renderLineWithIslamojis(comment.content, islamojiMap)}</p>
                      )}
                      {/* Comment like row — always visible */}
                      <div className="flex items-center gap-3 mt-1.5">
                        <button
                          onClick={() => handleLikeComment(comment.id)}
                          aria-label={commentLikes[comment.id]?.liked ? 'Unlike comment' : 'Like comment'}
                          className={`flex items-center gap-1 text-xs transition-colors ${
                            commentLikes[comment.id]?.liked ? 'text-primary' : 'text-text-muted hover:text-primary'
                          }`}
                          data-testid={`like-comment-${comment.id}`}
                        >
                          <Heart className={`w-3.5 h-3.5 ${commentLikes[comment.id]?.liked ? 'fill-current' : ''}`} aria-hidden="true" />
                          {(commentLikes[comment.id]?.count || 0) > 0 && (
                            <span aria-hidden="true">{commentLikes[comment.id].count}</span>
                          )}
                        </button>
                        <button
                          onClick={() => setReplyingTo(replyingTo?.id === comment.id ? null : { id: comment.id, display_name: comment.users?.display_name })}
                          aria-label="Reply to comment"
                          className="text-xs text-text-muted hover:text-primary transition-colors"
                        >
                          Reply
                        </button>
                      </div>
                      <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {/* Pin button — visible to premium post authors only */}
                        {isOwner && isPremium && (
                          <button
                            onClick={() => handlePinComment(comment.id)}
                            className={`p-1 rounded-full transition-colors ${comment.is_pinned ? 'text-primary hover:bg-primary/10' : 'text-text-muted hover:text-primary hover:bg-primary/10'}`}
                            data-testid={`pin-comment-${comment.id}`}
                            title={comment.is_pinned ? 'Unpin comment' : 'Pin comment'}
                          >
                            {comment.is_pinned ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}
                          </button>
                        )}
                        {canDeleteComment && (
                          <button
                            onClick={async () => {
                              if (!window.confirm('Delete this comment?')) return;
                              try {
                                await api.deleteComment(comment.id);
                                setComments(prev => prev.filter(c => c.id !== comment.id));
                                setCommentsCount(c => Math.max(0, c - 1));
                              } catch (err) {
                                alert(err?.response?.data?.detail || 'Failed to delete comment');
                              }
                            }}
                            className="p-1 rounded-full text-red-500 hover:bg-red-50 transition-colors"
                            data-testid={`delete-comment-${comment.id}`}
                            title="Delete comment"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Inline reply composer */}
                  {replyingTo?.id === comment.id && (
                    <div className="ml-11 mt-1">
                      <form onSubmit={handleAddReply} className="flex gap-2">
                        <img
                          src={profile?.avatar_url || defaultAvatar(profile?.gender)}
                          alt={profile?.display_name}
                          className="w-7 h-7 rounded-full object-cover flex-shrink-0 border border-primary"
                        />
                        <div className="flex-1 flex gap-2">
                          <CommentTextarea
                            autoFocus
                            inputRef={replyInputRef}
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                            placeholder={`Reply to ${replyingTo.display_name}…`}
                            ariaLabel={`Reply to ${replyingTo.display_name}`}
                            className="flex-1 px-3 py-1.5 bg-white/70 rounded-arch border border-primary/20 focus:ring-2 focus:ring-primary text-sm text-text-main"
                          />
                          <button
                            type="button"
                            onClick={() => setShowReplyIslamojis(s => !s)}
                            aria-label="Add Islamoji"
                            className={`p-1.5 rounded-full transition-colors ${showReplyIslamojis ? 'text-primary bg-primary/10' : 'text-text-muted hover:text-primary hover:bg-primary/10'}`}
                          >
                            <Moon className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="submit"
                            disabled={!replyText.trim() || loadingComment}
                            className="p-1.5 bg-primary text-primary-foreground rounded-full disabled:opacity-50"
                            aria-label="Send reply"
                          >
                            <Send className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </form>
                      {showReplyIslamojis && (
                        <IslamojiPicker onSelectEmoji={handleSelectReplyEmoji} showGifs={false} />
                      )}
                    </div>
                  )}

                  {/* Nested replies (1 level deep) */}
                  {(comment.replies || []).length > 0 && (
                    <div className="ml-11 space-y-2 mt-2">
                      {comment.replies.map((reply) => {
                        const canDeleteReply = reply.user_id === profile?.id || isAdmin || isModerator;
                        return (
                          <div key={reply.id} className="flex gap-2 group">
                            <img
                              src={reply.users?.avatar_url || defaultAvatar(reply.users?.gender)}
                              alt={reply.users?.display_name || 'User'}
                              className="w-7 h-7 rounded-full object-cover flex-shrink-0 border border-primary"
                            />
                            <div className="flex-1 rounded-arch p-2.5 bg-white/40 relative">
                              <p className="font-medium text-xs text-primary flex items-center gap-1 flex-wrap">
                                {reply.users?.display_name || 'Anonymous'}
                                <AdminBadge show={reply.users?.is_admin} size={9} />
                                <ModeratorBadge show={!reply.users?.is_admin && reply.users?.is_moderator} size={9} />
                                <MentorBadge show={!reply.users?.is_admin && !reply.users?.is_moderator && reply.users?.is_mentor} size={9} />
                              </p>
                              {getSoloIslamoji(reply.content, islamojiMap) ? (
                                <img
                                  src={getSoloIslamoji(reply.content, islamojiMap).image_url}
                                  alt={reply.content}
                                  className="w-11 h-11 mt-0.5"
                                  style={soloSayingStyle(getSoloIslamoji(reply.content, islamojiMap), 44)}
                                />
                              ) : (
                                <p className="text-xs text-text-main mt-0.5 whitespace-pre-wrap">{renderLineWithIslamojis(reply.content, islamojiMap)}</p>
                              )}
                              {canDeleteReply && (
                                <div className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <button
                                    onClick={async () => {
                                      if (!window.confirm('Delete this reply?')) return;
                                      try {
                                        await api.deleteComment(reply.id);
                                        setComments(prev => prev.map(c =>
                                          c.id === comment.id
                                            ? { ...c, replies: c.replies.filter(r => r.id !== reply.id) }
                                            : c
                                        ));
                                      } catch (err) {
                                        alert(err?.response?.data?.detail || 'Failed to delete reply');
                                      }
                                    }}
                                    className="p-1 rounded-full text-red-500 hover:bg-red-50 transition-colors"
                                    title="Delete reply"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  </div>
                  );
                })
              )}
            </div>

            {/* Add Comment Form */}
            <form onSubmit={handleAddComment} className="flex gap-3">
              <img
                src={profile?.avatar_url || defaultAvatar(profile?.gender)}
                alt={profile?.display_name}
                className="w-8 h-8 rounded-full object-cover flex-shrink-0 border border-primary"
              />
              <div className="flex-1 flex gap-2">
                <CommentTextarea
                  inputRef={commentInputRef}
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Write a comment..."
                  ariaLabel="Write a comment"
                  className="flex-1 px-4 py-2 bg-white/70 rounded-arch border border-primary/20 focus:ring-2 focus:ring-primary text-sm text-text-main"
                  testId={`comment-input-${post.id}`}
                />
                <button
                  type="button"
                  onClick={() => setShowCommentIslamojis(s => !s)}
                  aria-label="Add Islamoji"
                  className={`p-2 rounded-full transition-colors ${showCommentIslamojis ? 'text-primary bg-primary/10' : 'text-text-muted hover:text-primary hover:bg-primary/10'}`}
                >
                  <Moon className="w-4 h-4" />
                </button>
                <button
                  type="submit"
                  disabled={!newComment.trim() || loadingComment}
                  className="p-2 bg-primary text-primary-foreground rounded-full disabled:opacity-50"
                  data-testid={`submit-comment-${post.id}`}
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </form>
            {showCommentIslamojis && (
              <div className="ml-11">
                <IslamojiPicker onSelectEmoji={handleSelectCommentEmoji} showGifs={false} />
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Repost Modal — optional caption + success confirmation */}
      <AnimatePresence>
        {showRepostModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => !reposting && !repostSuccess && setShowRepostModal(false)}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
            data-testid={`repost-modal-${post.id}`}>
            <motion.div
              ref={repostModalRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby={`repost-modal-title-${post.id}`}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-card rounded-arch-xl shadow-2xl max-w-md w-full overflow-hidden">
              {/* Header */}
              <div className="px-5 py-4 border-b border-border flex items-center gap-2">
                <Share2 className="w-5 h-5 text-primary" aria-hidden="true" />
                <h3 id={`repost-modal-title-${post.id}`} className="font-heading text-lg text-primary">Share this post</h3>
                {!reposting && !repostSuccess && (
                  <button onClick={() => setShowRepostModal(false)}
                    aria-label="Close share dialog"
                    className="ml-auto min-h-[44px] min-w-[44px] flex items-center justify-center hover:bg-primary/10 rounded-full"
                    data-testid={`repost-modal-close-${post.id}`}>
                    <span className="text-xl leading-none" aria-hidden="true">&times;</span>
                  </button>
                )}
              </div>

              {repostSuccess ? (
                <div className="p-6 text-center" data-testid={`repost-success-${post.id}`}>
                  <div className="w-12 h-12 rounded-full bg-green-100 mx-auto flex items-center justify-center mb-3">
                    <Repeat2 className="w-6 h-6 text-green-700" />
                  </div>
                  <p className="text-sm text-text-main font-medium">Shared to your feed</p>
                  <p className="text-xs text-text-muted mt-1">Your repost is now visible to the community.</p>
                </div>
              ) : (
                <>
                  <div className="p-5 space-y-3">
                    <p className="text-xs text-text-muted">Add an optional message (visible above the original post)</p>
                    <textarea
                      value={repostComment}
                      onChange={(e) => setRepostComment(e.target.value)}
                      rows={3}
                      maxLength={500}
                      placeholder="Say something about this (optional)…"
                      className="w-full px-3 py-2 rounded-arch border border-primary/20 bg-white dark:text-gray-800 text-sm focus:ring-2 focus:ring-primary/40 resize-none"
                      data-testid={`repost-comment-input-${post.id}`}
                    />
                    <p className="text-[10px] text-text-muted text-right">{repostComment.length}/500</p>

                    {/* Original post preview */}
                    <div className="px-3 py-2 bg-primary/5 border border-primary/10 rounded-arch text-xs text-text-muted">
                      <p className="font-medium text-text-main mb-1">
                        {(isRepost ? post.original_author_name : author.display_name) || 'Anonymous'}
                      </p>
                      <p className="line-clamp-2 whitespace-pre-wrap">{post.content}</p>
                    </div>
                  </div>
                  <div className="px-5 py-3 border-t border-border flex justify-end gap-2">
                    <button
                      onClick={() => setShowRepostModal(false)}
                      disabled={reposting}
                      className="px-4 py-2 rounded-arch text-sm hover:bg-primary/5 disabled:opacity-50">
                      Cancel
                    </button>
                    <button
                      onClick={handleRepost}
                      disabled={reposting}
                      className="px-4 py-2 rounded-arch text-sm font-medium bg-primary text-white hover:bg-primary/90 flex items-center gap-2 disabled:opacity-50"
                      data-testid={`repost-submit-${post.id}`}>
                      <Repeat2 className="w-4 h-4" />
                      {reposting ? 'Sharing…' : 'Share'}
                    </button>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Visually hidden live region for screen reader announcements */}
      <span className="sr-only" aria-live="polite" aria-atomic="true">{announcement}</span>
    </motion.article>
  );
});
PostCard.displayName = 'PostCard';

export default PostCard;
